import * as Dialog from '@cannect/new-components/atoms/Dialog'
import {
  buildScreeningDestinationPath,
  createScreeningSession,
  getScreeningNext,
  postScreeningAnswer,
  postScreeningBack,
  postScreeningContact,
  SCREENING_TOKEN_KEY
} from '@cannect/services/resources/screening/requests'
import type { TScreeningNext, TScreeningQuestion, TWomanOption } from '@cannect/services/resources/screening/types'
import { useHistory } from 'react-router-dom'
import { useState, type ReactNode } from 'react'
import { clearMask, formatPhone } from 'utils/formatters'
import arrowLeft from './assets/arrow-left.svg'
import arrowRight from './assets/arrow-right.svg'
import arrowRightLight from './assets/arrow-right-light.svg'
import attentionIcon from './assets/attention.svg'
import closeIcon from './assets/close.svg'
import iconDoc from './assets/icon-doc.png'
import iconEval from './assets/icon-eval.png'
import iconRx from './assets/icon-rx.png'
import iconTreat from './assets/icon-treat.png'
import lockIcon from './assets/lock.svg'

const INPUT_CLASS =
  'h-12 w-full appearance-none rounded-xl border border-solid border-[#8f8b8a] bg-white px-4 text-base leading-[1.4] text-[#433f3e] outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-[#898483] hover:border-[#6b6665] focus:border-[#6b7b3c] focus:ring-4 focus:ring-[#a2b156]/30 aria-[invalid=true]:border-[#dc4e5a] aria-[invalid=true]:focus:ring-[#dc4e5a]/20 disabled:cursor-not-allowed disabled:border-[#e3e3e3] disabled:bg-[#f5f5f5] disabled:text-[#898483] sm:text-sm'

const DATE_INPUT_CLASS =
  'text-left [color-scheme:light] [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60 hover:[&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-date-and-time-value]:text-left'

const QuestionIntro = () => (
  <p className="w-full rounded-xl bg-[#e8edd9] px-4 py-3 text-sm font-medium leading-snug text-[#6b7b3c]">
    Para te direcionar para o melhor caminho, responda as perguntas a seguir!
  </p>
)

const WEIGHT_LOSS_HERO = 'peso'

const ATTENTION_BODY =
  'Este direcionamento não substitui uma avaliação profissional e não realiza diagnóstico. Ele ajuda você a encontrar o melhor caminho dentro da Cannect.'

// who + birthday + 3 campos de contato do adulto; menor tem 4, o progresso só se ajusta ao chegar lá.
const COLLECT_STEPS = 5

type Step = 'intent' | 'woman' | 'flow' | 'result'

const apiMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback

export const CarePathModal = ({
  open,
  onClose,
  initialHero
}: {
  open: boolean
  onClose: () => void
  initialHero?: string | null
}) => {
  const history = useHistory()
  const [step, setStep] = useState<Step>('intent')
  const [token, setToken] = useState('')
  const [next, setNext] = useState<TScreeningNext | null>(null)
  const [result, setResult] = useState<TScreeningNext | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [womanOptions, setWomanOptions] = useState<TWomanOption[]>([])
  const [symptomTotal, setSymptomTotal] = useState(0)
  const [answered, setAnswered] = useState(0)
  const [firstSymptomKey, setFirstSymptomKey] = useState<string | null>(null)

  const reset = () => {
    setStep('intent')
    setToken('')
    setNext(null)
    setResult(null)
    setError('')
    setWomanOptions([])
    setSymptomTotal(0)
    setAnswered(0)
    setFirstSymptomKey(null)
  }

  const close = () => {
    reset()
    onClose()
  }

  const leave = (path: string) => {
    close()
    history.push(path)
  }

  const advance = (payload: TScreeningNext, sessionToken: string) => {
    if (payload.done) {
      sessionStorage.setItem(SCREENING_TOKEN_KEY, sessionToken)
      setResult(payload)
      setStep('result')
      return
    }
    if (payload.phase === 'symptoms' && payload.question?.key) {
      setFirstSymptomKey((current) => current ?? payload.question.key)
    }
    setNext(payload)
  }

  const startSession = async (hero: string, moduleSlug?: string) => {
    setBusy(true)
    setError('')
    try {
      const response = await createScreeningSession(hero, moduleSlug)
      if (!response.success || !response.data?.token) {
        setError('Não foi possível iniciar a avaliação.')
        return
      }
      const sessionToken = response.data.token
      sessionStorage.setItem(SCREENING_TOKEN_KEY, sessionToken)
      setToken(sessionToken)
      setSymptomTotal(response.data.questions?.length || 0)
      setAnswered(0)
      setFirstSymptomKey(null)
      const first = await getScreeningNext(sessionToken)
      setStep('flow')
      advance(first, sessionToken)
    } catch (error) {
      const options = (error as { response?: { data?: { data?: { options?: TWomanOption[] } } } })?.response?.data?.data
        ?.options
      if (options?.length) {
        setWomanOptions(options)
        setStep('woman')
        return
      }
      setError(apiMessage(error, 'Não foi possível iniciar a avaliação.'))
    } finally {
      setBusy(false)
    }
  }

  const submit = async (value: unknown) => {
    if (!next?.question || !token) return
    setBusy(true)
    setError('')
    try {
      const { key } = next.question
      const payload =
        next.phase === 'symptoms'
          ? await postScreeningAnswer(token, { key, value })
          : await postScreeningContact(token, { [key]: value })
      setAnswered((current) => current + 1)
      advance(payload, token)
    } catch (error) {
      setError(apiMessage(error, 'Não foi possível salvar a resposta. Tente novamente.'))
    } finally {
      setBusy(false)
    }
  }

  const leaveSession = () => {
    if (womanOptions.length) {
      setToken('')
      setNext(null)
      setResult(null)
      setAnswered(0)
      setFirstSymptomKey(null)
      setStep('woman')
      return
    }
    reset()
  }

  const back = async () => {
    setError('')
    if (step === 'woman' || !token) {
      leaveSession()
      return
    }
    setBusy(true)
    try {
      const payload = await postScreeningBack(token)
      setAnswered((current) => Math.max(0, current - 1))
      setResult(null)
      setStep('flow')
      setNext(payload)
    } catch (error) {
      const message = apiMessage(error, '')
      if (message === 'Não há resposta anterior') {
        leaveSession()
        return
      }
      setError(apiMessage(error, 'Não foi possível voltar. Tente novamente.'))
    } finally {
      setBusy(false)
    }
  }
  const canGoBack = step === 'woman' || step === 'flow' || step === 'result'

  const progressTotal = 1 + COLLECT_STEPS + Math.max(symptomTotal, 1)
  const flowIndex = step === 'flow' ? 1 + answered : 0
  const progressIndex = step === 'result' ? progressTotal - 1 : flowIndex
  const progress = Math.round((Math.min(progressIndex, progressTotal - 1) / (progressTotal - 1)) * 100)

  const question = next?.question

  return (
    <Dialog.Root open={open} onOpenChange={(value) => !value && close()}>
      <Dialog.Content
        hideClose
        overlayClassName="bg-[rgba(67,63,62,0.2)] backdrop-blur-[8px]"
        className="left-0 top-0 flex h-dvh max-h-none w-full max-w-none translate-x-0 translate-y-0 flex-col gap-6 overflow-y-auto rounded-none border-0 bg-[#fafafa] px-[15px] py-8 shadow-none sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[90dvh] sm:w-[calc(100%-32px)] sm:max-w-[626px] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-[4px] sm:p-8">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            {canGoBack ? (
              <button
                type="button"
                onClick={() => {
                  back().catch(() => undefined)
                }}
                disabled={busy}
                className="inline-flex appearance-none items-center gap-1 rounded-[4px] border border-solid border-[#b5b3b3] px-2.5 py-1.5 text-xs leading-[1.4] text-[#898483] disabled:opacity-60">
                <img src={arrowLeft} alt="" width={14} height={14} />
                Voltar
              </button>
            ) : (
              <span />
            )}
            <Dialog.Close className="flex size-6 items-center justify-center" aria-label="Fechar">
              <img src={closeIcon} alt="" width={24} height={24} />
            </Dialog.Close>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-[16px] bg-[#e8edd9]">
            <div className="h-full rounded-[16px] bg-[#a2b156]" style={{ width: `${Math.max(progress, 8)}%` }} />
          </div>
        </div>

        <div className="flex flex-col gap-6">
          {step === 'intent' ? (
            <IntentStep
              busy={busy}
              onEval={() => {
                if (!initialHero) return
                startSession(initialHero).catch(() => undefined)
              }}
              onLeave={leave}
            />
          ) : null}
          {step === 'woman' ? (
            <div className="flex flex-col gap-4">
              <h2 className="text-lg font-semibold leading-[1.4] text-[#6b7b3c]">Qual cuidado você busca?</h2>
              <div className="grid gap-2">
                {womanOptions.map((option) => (
                  <OptionButton
                    key={option.slug}
                    image={iconEval}
                    label={option.name}
                    disabled={busy}
                    onClick={() => {
                      if (!initialHero) return
                      startSession(initialHero, option.slug).catch(() => undefined)
                    }}
                  />
                ))}
              </div>
            </div>
          ) : null}
          {step === 'flow' && question && next?.phase !== 'symptoms' ? (
            <CollectStep
              key={question.key}
              question={question}
              initialValue={next.value}
              busy={busy}
              intro={false}
              onSubmit={submit}
            />
          ) : null}
          {step === 'flow' && question && next?.phase === 'symptoms' ? (
            <QuestionStep
              key={question.key}
              question={question}
              initialValue={next.value}
              busy={busy}
              intro={question.key === firstSymptomKey}
              onSubmit={submit}
            />
          ) : null}
          {step === 'result' && result ? (
            <ResultStep
              recommended={result.destination === 'scheduling' ? 'scheduling' : 'instant'}
              schedulingOnly={initialHero === WEIGHT_LOSS_HERO}
              onChoose={(destination) =>
                leave(
                  buildScreeningDestinationPath(
                    result,
                    token,
                    initialHero === WEIGHT_LOSS_HERO ? 'scheduling' : destination
                  )
                )
              }
            />
          ) : null}
          {error ? <p className="text-sm text-[#dc4e5a]">{error}</p> : null}
          <Disclaimer />
        </div>
      </Dialog.Content>
    </Dialog.Root>
  )
}

const FIELD_COPY: Record<
  string,
  { label: string; placeholder?: string; input: string; error?: string; valid: (value: string) => boolean }
> = {
  date: { label: 'Data de nascimento', input: 'date', valid: (value) => Boolean(value) },
  phone: {
    label: 'Telefone',
    placeholder: '(11) 99999-9999',
    input: 'tel',
    error: 'Informe um telefone com DDD.',
    valid: (value) => clearMask(value).length >= 10
  },
  email: {
    label: 'E-mail',
    placeholder: 'nome@email.com',
    input: 'email',
    error: 'Informe um e-mail válido.',
    valid: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
  },
  text: {
    label: 'Nome completo',
    placeholder: 'Nome e sobrenome',
    input: 'text',
    valid: (value) => Boolean(value.trim())
  }
}

type Destination = 'instant' | 'scheduling'

const DESTINATION_COPY: Record<Destination, { title: string; description: string }> = {
  instant: {
    title: 'Falar com um generalista agora',
    description:
      'Caso você prefira, temos um time médico disponível para falar com você agora, atendimento por vídeo em poucos minutos.'
  },
  scheduling: {
    title: 'Agendar com um especialista',
    description: 'Consulta com um especialista, no dia e horário que você escolher.'
  }
}

const SCHEDULING_ONLY_DESCRIPTION =
  'Consulta com um especialista na sua condição, no horário que funcionar melhor para você.'

const ResultStep = ({
  recommended,
  schedulingOnly,
  onChoose
}: {
  recommended: Destination
  schedulingOnly?: boolean
  onChoose: (destination: Destination) => void
}) => {
  const order: Destination[] = schedulingOnly
    ? ['scheduling']
    : recommended === 'scheduling'
      ? ['scheduling', 'instant']
      : ['instant', 'scheduling']

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="text-xs leading-[1.4] text-[#898483]">Resultado</p>
        <h2 className="text-lg font-semibold leading-[1.4] text-[#6b7b3c]">Obrigado por compartilhar.</h2>
        <p className="text-sm leading-[1.4] text-[#898483]">
          Com base no que você contou, veja o caminho que faz mais sentido agora.
        </p>
      </div>
      <div
        className={
          order.length === 1 ? 'grid grid-cols-1 sm:max-w-sm' : 'grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2'
        }>
        {order.map((destination) => (
          <DestinationCard
            key={destination}
            recommended={destination === recommended || schedulingOnly}
            title={DESTINATION_COPY[destination].title}
            description={
              destination === 'scheduling' && schedulingOnly
                ? SCHEDULING_ONLY_DESCRIPTION
                : DESTINATION_COPY[destination].description
            }
            onClick={() => onChoose(destination)}
          />
        ))}
      </div>
      <p className="text-[10px] leading-[1.4] text-[#898483]">
        O que você respondeu segue junto para o profissional que te atender.
      </p>
    </div>
  )
}

const DestinationCard = ({
  recommended,
  title,
  description,
  onClick
}: {
  recommended: boolean
  title: string
  description: string
  onClick: () => void
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative flex h-full w-full appearance-none flex-col rounded-2xl border border-solid p-4 pt-[52px] text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6b7b3c] ${
      recommended
        ? 'border-[#aebe7b] bg-[#4b5a2e] hover:border-[#d7e0bd]'
        : 'border-[#bdc786] bg-[#e8edd9] hover:border-[#a2b156]'
    }`}>
    {recommended ? (
      <span className="absolute left-4 top-4 rounded-2xl bg-[#e8edd9] px-3 py-1.5 text-[10px] font-medium leading-[1.2] text-[#4b5a2e]">
        Recomendado
      </span>
    ) : null}
    <img src={iconDoc} alt="" width={40} height={40} className="size-10 shrink-0 object-contain" />
    <span
      className={`mt-3 min-h-[2.8em] text-sm font-semibold leading-[1.4] ${recommended ? 'text-[#e8edd9]' : 'text-[#53632e]'}`}>
      {title}
    </span>
    <span className={`mt-1 flex-1 text-xs leading-[1.4] ${recommended ? 'text-[#d7e0bd]' : 'text-[#433f3e]'}`}>
      {description}
    </span>
    <img
      src={recommended ? arrowRightLight : arrowRight}
      alt=""
      width={16}
      height={16}
      className="mt-4 size-4 shrink-0 self-end"
    />
  </button>
)

const sameChoice = (left: unknown, right: unknown) =>
  left === right || (left !== undefined && right !== undefined && String(left) === String(right))

const collectInputValue = (question: TScreeningQuestion, initialValue: unknown) => {
  if (initialValue === undefined || initialValue === null || initialValue === '') return ''
  if (question.type === 'phone') return formatPhone(String(initialValue))
  if (question.type === 'date') return String(initialValue).slice(0, 10)
  if (typeof initialValue === 'boolean') return ''
  return String(initialValue)
}

const CollectStep = ({
  question,
  initialValue,
  busy,
  intro,
  onSubmit
}: {
  question: TScreeningQuestion
  initialValue?: unknown
  busy: boolean
  intro?: boolean
  onSubmit: (value: unknown) => void
}) => {
  const [value, setValue] = useState(() => collectInputValue(question, initialValue))
  const options = Array.isArray(question.options) ? (question.options as { v: unknown; l: string }[]) : null

  if (options) {
    return (
      <div className="flex flex-col gap-4">
        {intro ? <QuestionIntro /> : null}
        {question.sub_text ? <p className="text-sm leading-[1.4] text-[#898483]">{question.sub_text}</p> : null}
        <h2 className="text-lg font-semibold leading-[1.4] text-[#6b7b3c]">{question.text}</h2>
        <div className="grid gap-2">
          {options.map((option) => (
            <OptionButton
              key={String(option.v)}
              image={iconEval}
              label={option.l}
              selected={sameChoice(option.v, initialValue)}
              disabled={busy}
              onClick={() => onSubmit(option.v)}
            />
          ))}
        </div>
      </div>
    )
  }

  const field = FIELD_COPY[question.type] || FIELD_COPY.text
  const isPhone = question.type === 'phone'
  const invalid = !field.valid(value)

  return (
    <FieldStep
      title={question.text}
      intro={intro}
      label={field.label}
      placeholder={field.placeholder}
      type={field.input}
      note={
        isPhone ? (
          <>
            Ao continuar, você concorda com o uso dos seus dados para este atendimento, conforme a{' '}
            <a href="/politica-de-privacidade" target="_blank" rel="noreferrer" className="text-[#53632e] underline">
              Política de privacidade
            </a>
            .
          </>
        ) : undefined
      }
      value={value}
      onChange={(input) => setValue(isPhone ? formatPhone(input) : input)}
      onNext={() => onSubmit(isPhone ? clearMask(value) : value.trim())}
      error={invalid && value.length > 0 ? field.error : undefined}
      disabled={busy || invalid}
    />
  )
}

const Disclaimer = () => (
  <div className="flex items-center gap-4 rounded-[4px] bg-[rgba(183,197,246,0.5)] px-4 py-2.5">
    <img src={attentionIcon} alt="" width={16} height={16} className="shrink-0" />
    <p className="text-[10px] leading-[1.2] text-[#587fea]">
      <span className="font-bold">ATENÇÃO: </span>
      {ATTENTION_BODY}
    </p>
  </div>
)

const OptionButton = ({
  image,
  label,
  selected,
  disabled,
  onClick
}: {
  image: string
  label: string
  selected?: boolean
  disabled?: boolean
  onClick: () => void
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={`flex w-full appearance-none items-center gap-2 rounded-2xl border border-solid p-2 text-left enabled:hover:border-[#a2b156] disabled:opacity-60 ${
      selected ? 'border-[#a2b156] bg-[#e8edd9]' : 'border-[#e3e3e3] bg-white'
    }`}>
    <img src={image} alt="" width={48} height={48} className="size-12 shrink-0 object-contain" />
    <span className="min-w-0 flex-1 text-sm leading-[1.4] text-[#433f3e]">{label}</span>
    <img src={arrowRight} alt="" width={16} height={16} className="shrink-0" />
  </button>
)

const BUTTON_TONE = {
  muted: 'border-[#a69e89] bg-[#dbd8d0] text-[#a69e89]',
  solid: 'border-[#aebe7b] bg-[#4b5a2e] text-[#aebe7b]',
  soft: 'border-[#aebe7b] bg-[#d7e0bd] text-[#53632e]'
} as const

const ContinueButton = ({
  children,
  disabled,
  onClick,
  tone
}: {
  children: ReactNode
  disabled?: boolean
  onClick: () => void
  tone: keyof typeof BUTTON_TONE
}) => {
  const resolved = disabled ? 'muted' : tone

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex w-fit max-w-full appearance-none items-end gap-4 self-start rounded-2xl border border-solid p-2 text-left sm:gap-8 ${BUTTON_TONE[resolved]}`}>
      <span className="inline-flex items-center gap-2">
        <img src={iconDoc} alt="" width={48} height={48} className="size-12 shrink-0 object-contain" />
        <span className="max-w-[158px] text-sm leading-normal">{children}</span>
      </span>
      <img
        src={resolved === 'solid' ? arrowRightLight : arrowRight}
        alt=""
        width={16}
        height={16}
        className="shrink-0"
      />
    </button>
  )
}

const IntentStep = ({
  onEval,
  onLeave,
  busy
}: {
  onEval: () => void
  onLeave: (path: string) => void
  busy: boolean
}) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col gap-1">
      <p className="text-xs leading-[1.4] text-[#898483]">Olá, que bom ter você aqui</p>
      <h2 className="text-lg font-semibold leading-[1.4] text-[#6b7b3c]">Como podemos te ajudar hoje?</h2>
      <p className="text-sm leading-[1.4] text-[#433f3e]">
        Para te ajudar melhor, precisamos coletar alguns dados sobre você para auxiliar no atendimento.
      </p>
    </div>
    <div className="grid gap-2">
      <OptionButton image={iconEval} label="Busco uma avaliação profissional" onClick={onEval} disabled={busy} />
      <OptionButton image={iconRx} label="Tenho uma receita e quero comprar" onClick={() => onLeave('/produtos')} />
      <OptionButton image={iconTreat} label="Busco um produto" onClick={() => onLeave('/produtos')} />
    </div>
  </div>
)

const FieldStep = ({
  title,
  intro,
  label,
  placeholder,
  hint,
  note,
  type = 'text',
  value,
  onChange,
  onNext,
  error,
  disabled,
  cta = 'Continuar'
}: {
  title: string
  intro?: boolean
  label: string
  placeholder?: string
  hint?: string
  note?: ReactNode
  type?: string
  value: string
  onChange: (value: string) => void
  onNext: () => void
  error?: string
  disabled: boolean
  cta?: string
}) => {
  const [touched, setTouched] = useState(false)
  const showError = touched && Boolean(error)

  return (
    <div className="flex flex-col gap-6">
      <div className={intro ? 'flex flex-col gap-4' : 'flex flex-col gap-1'}>
        {intro ? <QuestionIntro /> : null}
        <h2 className="text-lg font-semibold leading-[1.4] text-[#6b7b3c]">{title}</h2>
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium leading-[1.4] text-[#433f3e]" htmlFor="care-path-field">
          {label}
          <span className="ml-0.5 text-[#dc4e5a]">*</span>
        </label>
        <input
          id="care-path-field"
          className={type === 'date' ? `${INPUT_CLASS} ${DATE_INPUT_CLASS}` : INPUT_CLASS}
          type={type}
          placeholder={placeholder}
          value={value}
          aria-invalid={showError}
          aria-describedby={showError ? 'care-path-field-error' : undefined}
          max={type === 'date' ? new Date().toISOString().slice(0, 10) : undefined}
          onChange={(event) => onChange(event.target.value)}
          onBlur={() => setTouched(true)}
          onKeyDown={(event) => event.key === 'Enter' && !disabled && onNext()}
        />
        {showError ? (
          <p id="care-path-field-error" role="alert" className="text-sm leading-[1.4] text-[#b73a45]">
            {error}
          </p>
        ) : null}
        {!showError && hint ? <p className="text-sm leading-[1.4] text-[#6b6665]">{hint}</p> : null}
      </div>
      {note ? (
        <div className="flex w-full items-start gap-3 rounded-xl border border-solid border-[#e8edd9] bg-[#f4f6ec] px-4 py-3">
          <img src={lockIcon} alt="" width={16} height={16} className="mt-0.5 shrink-0" />
          <p className="flex-1 text-sm leading-[1.5] text-[#6b6665]">{note}</p>
        </div>
      ) : null}
      <ContinueButton tone="solid" disabled={disabled} onClick={onNext}>
        {cta}
      </ContinueButton>
    </div>
  )
}

const plainText = (value: string) => value.replace(/<[^>]+>/g, '')

type QuestionScale = {
  min?: number
  max?: number
  lo?: string
  hi?: string
  left?: string
  right?: string
  ph?: string
  unit?: string
  noneLabel?: string
  groups?: { title?: string; items?: Array<string | { id?: string; l?: string }> }[]
}

const itemKey = (item: string | { id?: string; l?: string }) =>
  typeof item === 'string' ? item : item.id || item.l || ''

const itemLabel = (item: string | { id?: string; l?: string }) =>
  typeof item === 'string' ? item : item.l || item.id || ''

const questionInitial = (question: TScreeningQuestion, initialValue: unknown, isScale: boolean, min: number) => {
  if (initialValue !== undefined && initialValue !== null) {
    if (question.type === 'imc' && typeof initialValue === 'object') {
      const body = initialValue as { peso?: number | string; altura?: number | string }
      return {
        peso: body.peso === undefined || body.peso === null ? '' : String(body.peso),
        altura: body.altura === undefined || body.altura === null ? '' : String(body.altura)
      }
    }
    return initialValue
  }
  if (isScale) return min
  if (question.type === 'check' || question.type === 'multi') return []
  return undefined
}

const QuestionStep = ({
  question,
  initialValue,
  busy,
  intro,
  onSubmit
}: {
  question: TScreeningQuestion
  initialValue?: unknown
  busy: boolean
  intro?: boolean
  onSubmit: (value: unknown) => void
}) => {
  const options = Array.isArray(question.options)
    ? (question.options as { id?: string; v: unknown; l: string }[])
    : null
  const isMulti = question.type === 'multi'
  const scale = question.options && !Array.isArray(question.options) ? (question.options as QuestionScale) : null
  const isScale = question.type === 'nrs' || question.type === 'slider' || question.type === 'cat'
  const min = Number(scale?.min ?? 0)
  const max = Number(scale?.max ?? (question.type === 'cat' ? 5 : 10))
  const [value, setValue] = useState<unknown>(() => questionInitial(question, initialValue, isScale, min))
  const selected = Array.isArray(value) ? value.map(String) : []
  const isImc = question.type === 'imc'
  const body = isImc ? ((value || {}) as { peso?: string; altura?: string }) : {}
  const peso = Number(String(body.peso ?? '').replace(',', '.'))
  const altura = Number(String(body.altura ?? '').replace(',', '.'))
  const imcReady = peso >= 20 && peso <= 400 && altura >= 80 && altura <= 250
  const imc = imcReady ? peso / (altura / 100) ** 2 : null
  const needsText =
    ((question.type === 'num' || question.type === 'text') && !String(value ?? '').trim()) || (isImc && !imcReady)
  const lo = scale?.lo || scale?.left || String(min)
  const hi = scale?.hi || scale?.right || String(max)
  const sliderFill = max > min && typeof value === 'number' ? ((value - min) / (max - min)) * 100 : 0

  return (
    <div className="flex flex-col gap-6">
      <div className={intro || question.sub_text ? 'flex flex-col gap-4' : 'flex flex-col gap-1'}>
        {intro ? <QuestionIntro /> : null}
        <div className="flex flex-col gap-1">
          {question.sub_text ? (
            <p className="text-sm leading-[1.4] text-[#898483]">{plainText(question.sub_text)}</p>
          ) : null}
          <h2 className="text-lg font-semibold leading-[1.4] text-[#6b7b3c]">{plainText(question.text)}</h2>
        </div>
      </div>
      <div className="flex flex-col gap-4 rounded-2xl border border-solid border-[#e8edd9] bg-white p-5">
        {isScale ? <p className="text-sm font-semibold leading-[1.4] text-[#433f3e]">arraste para escolher</p> : null}
        {isScale ? (
          <>
            <input
              aria-label={plainText(question.text)}
              type="range"
              min={min}
              max={max}
              value={typeof value === 'number' ? value : min}
              onChange={(event) => setValue(Number(event.target.value))}
              style={{
                background: `linear-gradient(to right, #a2b156 ${sliderFill}%, #e8edd9 ${sliderFill}%)`
              }}
              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-[#e8edd9] focus:outline-none [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-[3px] [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-[#6b7b3c] [&::-webkit-slider-thumb]:h-7 [&::-webkit-slider-thumb]:w-7 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-solid [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-[#6b7b3c] [&::-webkit-slider-thumb]:shadow-[0_1px_4px_rgba(67,63,62,0.35)] focus-visible:[&::-webkit-slider-thumb]:ring-4 focus-visible:[&::-webkit-slider-thumb]:ring-[#a2b156]/40"
            />
            <div className="flex justify-between text-xs leading-[1.4] text-[#6b6665]">
              <span>{lo}</span>
              <span className="text-sm font-semibold text-[#4b5a2e]">{String(value)}</span>
              <span>{hi}</span>
            </div>
          </>
        ) : null}
        {options && isMulti ? (
          <div className="flex flex-col gap-1">
            <p className="text-xs leading-[1.4] text-[#6b6665]">Você pode marcar mais de uma opção.</p>
            {options.map((option) => {
              const key = String(option.id ?? option.v)
              const checked = selected.includes(key)
              return (
                <label
                  key={key}
                  htmlFor={`multi-${key}`}
                  className="flex min-h-11 cursor-pointer items-center gap-3 rounded-xl px-3 text-sm text-[#433f3e] hover:bg-[#f4f6ec] has-[:checked]:bg-[#e8edd9] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#6b7b3c]">
                  <input
                    id={`multi-${key}`}
                    type="checkbox"
                    className="size-5 shrink-0 cursor-pointer accent-[#6b7b3c]"
                    checked={checked}
                    onChange={() => setValue(checked ? selected.filter((entry) => entry !== key) : [...selected, key])}
                  />
                  {option.l}
                </label>
              )
            })}
          </div>
        ) : null}
        {options && !isMulti ? (
          <div className="flex flex-col gap-2">
            {options.map((option) => (
              <button
                key={String(option.v)}
                type="button"
                disabled={busy}
                onClick={() => onSubmit(option.v)}
                className={`appearance-none rounded-2xl border border-solid px-3 py-3 text-left text-sm text-[#433f3e] enabled:hover:border-[#a2b156] disabled:opacity-60 ${
                  sameChoice(option.v, value ?? initialValue)
                    ? 'border-[#a2b156] bg-[#e8edd9]'
                    : 'border-[#d7e0bd] bg-white'
                }`}>
                {option.l}
              </button>
            ))}
          </div>
        ) : null}
        {question.type === 'check' && scale?.groups
          ? scale.groups.map((group, groupIndex) => (
              <div key={group.title || groupIndex}>
                {group.title ? <p className="mb-2 text-xs text-[#6b6665]">{group.title}</p> : null}
                <div className="flex flex-col gap-1">
                  {(group.items || []).map((item) => {
                    const key = itemKey(item)
                    const checked = selected.includes(key)
                    return (
                      <label
                        key={key}
                        className="flex min-h-11 cursor-pointer items-center gap-3 rounded-xl px-3 text-sm text-[#433f3e] hover:bg-[#f4f6ec] has-[:checked]:bg-[#e8edd9] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#6b7b3c]">
                        <input
                          type="checkbox"
                          className="size-5 shrink-0 cursor-pointer accent-[#6b7b3c]"
                          checked={checked}
                          onChange={() =>
                            setValue(checked ? selected.filter((entry) => entry !== key) : [...selected, key])
                          }
                        />
                        {itemLabel(item)}
                      </label>
                    )
                  })}
                </div>
              </div>
            ))
          : null}
        {scale?.noneLabel ? <p className="text-[10px] text-[#898483]">{scale.noneLabel}</p> : null}
        {isImc ? (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <label htmlFor="care-path-peso" className="flex flex-col gap-1.5 text-sm font-medium text-[#433f3e]">
                Peso (kg)
                <input
                  id="care-path-peso"
                  className={INPUT_CLASS}
                  type="number"
                  inputMode="decimal"
                  min={20}
                  max={400}
                  placeholder="70"
                  value={body.peso ?? ''}
                  onChange={(event) => setValue({ ...body, peso: event.target.value })}
                />
              </label>
              <label htmlFor="care-path-altura" className="flex flex-col gap-1.5 text-sm font-medium text-[#433f3e]">
                Altura (cm)
                <input
                  id="care-path-altura"
                  className={INPUT_CLASS}
                  type="number"
                  inputMode="numeric"
                  min={80}
                  max={250}
                  placeholder="170"
                  value={body.altura ?? ''}
                  onChange={(event) => setValue({ ...body, altura: event.target.value })}
                />
              </label>
            </div>
            <p className="text-xs leading-[1.4] text-[#6b6665]">
              {imc === null
                ? 'Informe peso entre 20 e 400 kg e altura entre 80 e 250 cm.'
                : `Seu IMC: ${imc.toFixed(1).replace('.', ',')} kg/m²`}
            </p>
          </div>
        ) : null}
        {question.type === 'num' || question.type === 'text' ? (
          <input
            className={INPUT_CLASS}
            type={question.type === 'num' ? 'number' : 'text'}
            placeholder={scale?.ph}
            value={value === undefined ? '' : String(value)}
            onChange={(event) => setValue(question.type === 'num' ? Number(event.target.value) : event.target.value)}
          />
        ) : null}
      </div>
      {options && !isMulti ? null : (
        <ContinueButton
          tone="solid"
          disabled={busy || needsText || (isMulti && selected.length === 0)}
          onClick={() => onSubmit(isImc ? { peso, altura } : value)}>
          Continuar
        </ContinueButton>
      )}
    </div>
  )
}
